import { gql, useMutation, useQuery } from '@apollo/client';
import { AccountGroupTypes } from '@interfaces/accountGroup';
import type { TranslatedString } from '@interfaces/shared';
import { EntityTypes } from '@interfaces/user';
import { CreateAccountGroupVisual } from './createAccountGroup.visual';

const FETCH_USER = gql`
  query fetchUser($input: FetchUserInput!) {
    fetchUser(input: $input) {
      user {
        id
        type
      }
    }
  }
`;

const CREATE_ACCOUNT_GROUP = gql`
  mutation createAccountGroup($input: CreateAccountGroupInput!) {
    createAccountGroup(input: $input) {
      accountGroup {
        id
      }
    }
  }
`;

export type AccountGroupWorkflowOptions = {
  title: TranslatedString;
  subTitle: TranslatedString;
  accountGroupSection: { enabled: boolean };
  ungroupedAccounts: { enabled: boolean };
};

export const CreateAccountGroup = ({
  options,
  objectId,
  onNext,
  stepLoading,
  objectType = 'USER',
}: {
  options: AccountGroupWorkflowOptions;
  objectId: string;
  onNext: () => void;
  stepLoading: boolean;
  objectType?: 'USER' | 'HOUSEHOLD' | 'ORGANIZATION';
}) => {
  const { data: userData } = useQuery(FETCH_USER, {
    variables: { input: { userId: objectId } },
    skip: objectType !== 'USER',
  });

  const [createAccountGroup, { loading }] = useMutation(CREATE_ACCOUNT_GROUP, {
    onCompleted: () => {
      onNext();
    },
  });

  const onContinue = async (updatedAccountGroup: {
    translatedName: TranslatedString;
    accountIds: string[];
  }) => {
    const userType =
      userData?.fetchUser?.user?.type === EntityTypes.INDIVIDUAL
        ? AccountGroupTypes.INDIVIDUAL
        : AccountGroupTypes.NON_INDIVIDUAL;
    await createAccountGroup({
      variables: {
        input: {
          ...(objectType === 'USER'
            ? { userId: objectId }
            : objectType === 'HOUSEHOLD'
              ? { householdClientGroupId: objectId }
              : { organizationId: objectId }),
          type:
            objectType === 'HOUSEHOLD'
              ? AccountGroupTypes.HOUSEHOLD
              : objectType === 'ORGANIZATION'
                ? AccountGroupTypes.ORGANIZATION
                : userType,
          translatedName: updatedAccountGroup.translatedName,
          accountIds: updatedAccountGroup.accountIds,
        },
      },
    });
  };

  return (
    <CreateAccountGroupVisual
      options={options}
      onContinue={onContinue}
      loading={stepLoading || loading}
      objectType={objectType}
      objectId={objectId}
    />
  );
};
